import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { StageId } from '@joker/domain'
import { useTRPC } from './trpc'
import { Suit, type SuitName } from './ui'
import { Splash } from './Splash'
import { Login } from './Login'
import { Pipeline } from './views/Pipeline'
import { Cohorts } from './views/Cohorts'
import { Retention } from './views/Retention'
import { Stats } from './views/Stats'
import { People, INITIAL_PEOPLE_VIEW, type PeopleView } from './views/People'
import { Person } from './views/Person'
import { CohortList } from './views/CohortList'
import { CohortPage } from './views/CohortPage'
import { Optometrists } from './views/Optometrists'
import { Practice } from './views/Practice'
import { Prospects } from './views/Prospects'
import { Purchases } from './views/Purchases'
import { Usage } from './views/Usage'
import { Health } from './views/Health'
import { Jobs } from './views/Jobs'
import { ProgramPage } from './views/Program'
import { useRoute, tabOf, type Tab, type PatientFilters, type TrainedWithin } from './route'

type Light = 'ok' | 'warn' | 'bad'

/* One mark per section. A deck has six — four suits and two jokers — and
   Optometrists makes seven, so it takes the card back: it is the one section
   about where the cards came from rather than about the cards. Stats is the
   eighth and takes the whole hand, being about all of them at once. */
const TABS: Array<{ id: Tab; label: string; suit: SuitName }> = [
  { id: 'pipeline', label: 'Pipeline', suit: 'spade' },
  { id: 'cohorts', label: 'Cohorts', suit: 'heart' },
  { id: 'retention', label: 'Retention', suit: 'diamond' },
  { id: 'stats', label: 'Stats', suit: 'hand' },
  { id: 'usage', label: 'Usage', suit: 'diamond' },
  { id: 'prospects', label: 'Leads', suit: 'joker-black' },
  { id: 'purchases', label: 'Direct purchases', suit: 'diamond' },
  { id: 'people', label: 'Patients', suit: 'club' },
  { id: 'optometrists', label: 'Optometrists', suit: 'card-back' },
  { id: 'jobs', label: 'Jobs', suit: 'joker-red' },
  { id: 'health', label: 'Data health', suit: 'joker-red' },
]

function Console({ email }: { email: string }) {
  /* Where we are lives in the address bar, not in memory: Back and Forward walk
     the path taken, and any screen can be copied and sent. See route.ts. */
  const { route, navigate, back } = useRoute()
  /* The People table's own sort and page live here so they survive opening a
     person's record, which unmounts that view entirely. Not in the address:
     a shared link names the question, not the page somebody had reached. */
  const [peopleView, setPeopleView] = useState<PeopleView>(INITIAL_PEOPLE_VIEW)

  const trpc = useTRPC()
  const health = useQuery({ ...trpc.health.data.queryOptions(), retry: false, refetchInterval: 60_000 })
  const jobs = useQuery({ ...trpc.jobs.overview.queryOptions(), retry: false, refetchInterval: 60_000 })

  /* One colour per monitoring tab: red when something is broken, amber when
     something is behind or still running, green when all is well. */
  const sourceStatuses = health.data?.sources.map((s) => s.status) ?? []
  const healthLight: Light | null = !health.data
    ? null
    : sourceStatuses.some((s) => s === 'failing' || s === 'never')
      ? 'bad'
      : sourceStatuses.some((s) => s === 'stale')
        ? 'warn'
        : 'ok'
  const jobsLight: Light | null = !jobs.data
    ? null
    : jobs.data.some((j) => j.lastRun?.status === 'failed' || j.configured === false)
      ? 'bad'
      : jobs.data.some((j) => j.lastRun?.status === 'running' || (j.lastRun?.failed ?? 0) > 0)
        ? 'warn'
        : 'ok'

  const go = (t: Tab) => navigate({ page: 'tab', tab: t })
  const openPerson = (id: string) => navigate({ page: 'person', id })
  const openOrder = (id: string) => navigate({ page: 'order', id })
  const openPracticeEditor = (id: string) => navigate({ page: 'practice', id })

  /* Each way into the Patients table is one question: a bucket, a stage, a
     practice, an age band, a revenue period, a Usage segment or window. Opening
     one clears the others — otherwise the table silently shows their
     intersection — and returns to the first page. */
  const openPatients = (filters: PatientFilters) => {
    setPeopleView((v) => ({ ...v, page: 0 }))
    navigate({ page: 'tab', tab: 'people', filters })
  }
  const filters: PatientFilters = route.page === 'tab' && route.tab === 'people' ? (route.filters ?? {}) : {}
  /* Changing one part of the current question, from the table's own controls. */
  const setFilters = (patch: Partial<PatientFilters>) => {
    const next = { ...filters, ...patch }
    for (const k of Object.keys(next) as (keyof PatientFilters)[]) if (next[k] === undefined) delete next[k]
    navigate({ page: 'tab', tab: 'people', filters: next }, { replace: true })
  }

  const openBucket = (id: string) => openPatients({ bucketId: id })
  const openPractice = (id: string) => openPatients({ practiceId: id })
  /* A bucket about prospective users belongs on their screen, not the patients
     table — opened there it either shows nothing or, worse, labels a waitlist
     signup "1 patient". */
  const openProspectBucket = (id: string) => navigate({ page: 'tab', tab: 'prospects', leadBucketId: id })
  const openAgeBand = (id: string, paid = false) => openPatients({ ageBand: id, ...(paid ? { paidOnly: true } : {}) })
  /* A column on either revenue chart opens the people behind it. */
  const openPaidIn = (period: string) => openPatients({ paidIn: period })
  /* A tile on the Usage "still training" sheet opens the patients behind it,
     with the paying-only toggle, so the list matches the number pressed. */
  const openTrainedWithin = (id: TrainedWithin, paid = false) =>
    openPatients({ trainedWithin: id, ...(paid ? { paidOnly: true } : {}) })
  const openSegment = (seq: number) => openPatients({ segment: seq })
  const openStage = (id: StageId) => {
    /* A lead has no Oryx account, so the Patients table is the wrong list for
       it. It goes to the Leads screen, which is the same people. */
    if (id === 'lead') return go('prospects')
    openPatients({ stage: id })
  }

  /* Back to whichever screen opened this list: the browser's own Back when it
     came from inside Joker, otherwise the screen that question belongs to. */
  const backToSource = () =>
    back(
      filters.practiceId
        ? { page: 'tab', tab: 'optometrists' }
        : filters.bucketId
          ? { page: 'cohort', id: filters.bucketId }
          : filters.ageBand || filters.paidIn
            ? { page: 'tab', tab: 'stats' }
            : filters.segment != null || filters.trainedWithin
              ? { page: 'tab', tab: 'usage' }
              : { page: 'tab', tab: 'people' },
    )

  return (
    <>
      <Splash />
      <header className="topbar">
        <div className="topbar-inner">
          <div className="brand">
            <span className="brand-card" aria-hidden="true">
              J
            </span>
            <b>Joker</b>
            <span className="sub">Oryx ops console</span>
          </div>
          <div className="signout">
            <span>{email}</span>
            <button className="btn sm ghost" onClick={() => void signOut()}>
              Sign out
            </button>
          </div>
        </div>
        <nav className="tabs" role="tablist">
          <div className="seg">
            {TABS.map((t) => (
              <button
                key={t.id}
                role="tab"
                aria-selected={tabOf(route) === t.id && route.page === 'tab'}
                onClick={() => go(t.id)}
              >
                <Suit name={t.suit} />
                {t.label}
                {t.id === 'health' && healthLight && <span className={`dot ${healthLight}`} style={{ marginLeft: 6 }} />}
                {t.id === 'jobs' && jobsLight && <span className={`dot ${jobsLight}`} style={{ marginLeft: 6 }} />}
              </button>
            ))}
          </div>
        </nav>
      </header>

      <main>
        {route.page === 'person' ? (
          <Person
            key={route.id}
            id={route.id}
            onBack={() => back({ page: 'tab', tab: 'people' })}
            onOpenPerson={openPerson}
            onOpenOrder={openOrder}
            onOpenProgram={(pid) => navigate({ page: 'program', id: pid })}
            backLabel="Back"
          />
        ) : route.page === 'practice' ? (
          <Practice
            id={route.id}
            onBack={() => back({ page: 'tab', tab: 'optometrists' })}
            onOpenPerson={openPerson}
          />
        ) : route.page === 'order' ? (
          <Purchases
            orderId={route.id}
            onOpenOrder={openOrder}
            onBack={() => back({ page: 'tab', tab: 'purchases' })}
            onOpenPerson={openPerson}
          />
        ) : route.page === 'program' ? (
          <ProgramPage id={route.id} onBack={() => back({ page: 'tab', tab: 'people' })} onOpenPerson={openPerson} />
        ) : route.page === 'cohort' ? (
          <CohortPage
            key={route.id}
            id={route.id}
            onBack={() => back({ page: 'tab', tab: 'cohorts' })}
            onOpenMembers={(b) => (b.audience === 'prospects' ? openProspectBucket(b.id) : openBucket(b.id))}
            onOpenPerson={openPerson}
          />
        ) : route.page === 'cohortCompare' ? (
          <>
            <button type="button" className="back" onClick={() => back({ page: 'tab', tab: 'cohorts' })}>
              ← Cohorts
            </button>
            <Cohorts />
          </>
        ) : route.tab === 'pipeline' ? (
          <Pipeline onOpenBucket={openBucket} onOpenStage={openStage} />
        ) : route.tab === 'cohorts' ? (
          <CohortList
            onOpen={(id) => navigate({ page: 'cohort', id })}
            onCompare={() => navigate({ page: 'cohortCompare' })}
          />
        ) : route.tab === 'retention' ? (
          <Retention />
        ) : route.tab === 'usage' ? (
          <Usage onOpenPerson={openPerson} onOpenSegment={openSegment} onOpenTrainedWithin={openTrainedWithin} />
        ) : route.tab === 'stats' ? (
          <Stats onOpenAgeBand={openAgeBand} onOpenPaidIn={openPaidIn} />
        ) : route.tab === 'people' ? (
          <People
            {...(filters.bucketId ? { bucketId: filters.bucketId } : {})}
            {...(filters.practiceId ? { practiceId: filters.practiceId } : {})}
            {...(filters.ageBand ? { ageBand: filters.ageBand } : {})}
            {...(filters.segment != null ? { segment: filters.segment } : {})}
            {...(filters.trainedWithin ? { trainedWithin: filters.trainedWithin } : {})}
            onClearTrainedWithin={() => setFilters({ trainedWithin: undefined })}
            {...(filters.paidIn ? { paidIn: filters.paidIn } : {})}
            onClearPaidIn={() => setFilters({ paidIn: undefined })}
            onClearSegment={() => setFilters({ segment: undefined })}
            {...(filters.paidOnly ? { paidOnly: filters.paidOnly } : {})}
            {...(filters.stage ? { stage: filters.stage } : {})}
            onStageChange={(stage) => setFilters({ stage })}
            onClearBucket={() => setFilters({ bucketId: undefined })}
            onClearPractice={() => setFilters({ practiceId: undefined })}
            onClearAgeBand={() => setFilters({ ageBand: undefined, paidOnly: undefined })}
            onBackToSource={backToSource}
            onOpenPerson={openPerson}
            view={peopleView}
            onViewChange={setPeopleView}
          />
        ) : route.tab === 'prospects' ? (
          <Prospects
            onOpenPerson={openPerson}
            {...(route.leadBucketId ? { bucketId: route.leadBucketId } : {})}
            onClearBucket={() => navigate({ page: 'tab', tab: 'prospects' }, { replace: true })}
            onOpenPurchases={() => go('purchases')}
            onOpenOrder={openOrder}
          />
        ) : route.tab === 'purchases' ? (
          <Purchases onOpenOrder={openOrder} onOpenPerson={openPerson} />
        ) : route.tab === 'optometrists' ? (
          <Optometrists onOpenPractice={openPractice} onEditPractice={openPracticeEditor} onOpenPerson={openPerson} />
        ) : route.tab === 'jobs' ? (
          <Jobs onOpenPerson={openPerson} />
        ) : (
          <Health />
        )}
      </main>
    </>
  )
}

/* A reload after signing out drops every patient record held in memory,
   rather than trusting each cache to be cleared. */
async function signOut(): Promise<void> {
  await fetch('/api/auth/logout', { method: 'POST' }).catch(() => {})
  window.location.reload()
}

const isUnauthorized = (err: unknown) => String(err).includes('UNAUTHORIZED') || String((err as { data?: { code?: string } })?.data?.code) === 'UNAUTHORIZED'

type SessionState = { state: 'none' | 'locked' | 'active'; email?: string }
const SESSION_KEY = ['auth-session']

async function fetchSession(): Promise<SessionState> {
  const res = await fetch('/api/auth/session')
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return (await res.json()) as SessionState
}

/**
 * Nothing renders behind the sign-in screen: the console mounts only while the
 * session is active. A remembered browser that has locked (8 hours unused, or
 * no code today) gets the code-only screen. With AUTH_DISABLED the API answers
 * "active" for everyone, so local development never sees either screen.
 */
export function App() {
  const qc = useQueryClient()
  /* Every minute, so an idle open tab hides its data once the session locks.
     Asking does not count as use, so this never keeps a session alive. */
  const session = useQuery({ queryKey: SESSION_KEY, queryFn: fetchSession, refetchInterval: 60_000, retry: false })

  /* Any call refused for want of a session asks again where the browser stands. */
  useEffect(
    () =>
      qc.getQueryCache().subscribe((event) => {
        if (event.type !== 'updated' || event.action.type !== 'error') return
        if (isUnauthorized(event.action.error)) void qc.invalidateQueries({ queryKey: SESSION_KEY })
      }),
    [qc],
  )

  if (session.isPending) return null
  if (session.error) {
    return <p className="login-error" style={{ padding: 24 }}>Could not reach Joker: {String(session.error.message)}</p>
  }
  const signedIn = () => {
    /* Drop everything cached, including whatever failed while locked. */
    qc.removeQueries({ predicate: (q) => q.queryKey[0] !== SESSION_KEY[0] })
    void qc.invalidateQueries({ queryKey: SESSION_KEY })
  }
  if (session.data.state === 'active') return <Console email={session.data.email ?? ''} />
  if (session.data.state === 'locked') return <Login key="reverify" reverifyEmail={session.data.email} onSignedIn={signedIn} />
  return <Login key="full" onSignedIn={signedIn} />
}

